import { Router } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { UIMenuSection as UIMenuSectionTypes } from '@creoate/virgo-content-api';

// mui
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Stack from '@mui/material/Stack';
import Portal from '@mui/material/Portal';
import Drawer from '@mui/material/Drawer';
import CloseIcon from '@mui/icons-material/Close';

// hooks
import useVirgoContent from 'hooks/useVirgoContent';

// components
import BackButton from './BackButton';
import UIMenuSection from './UIMenuSection';
import MobileTopMenuHome from './TopMenuHome';
import TopMenuMobileLink from './TopMenuMobileLink';
import UIMenuSandboxSection from './UIMenuSandboxSection';

// utils
import { DrawerAppBarProps, INavLink } from '../utils/types';

const DRAWER_WIDTH = '85%';

const DrawerAppBar = ({ closeIconRef, onClose, showTopMenuDrawer }: DrawerAppBarProps) => {
  const { topMenu } = useVirgoContent();

  const [selectedNavLinkItem, setSelectedNavLinkItem] = useState<INavLink | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const handleTopLevelClick = (data: INavLink): void => setSelectedNavLinkItem(data);

  const handleClose = useCallback(() => {
    setSelectedCategory('');
    setSelectedNavLinkItem(null);
    onClose();
  }, [onClose]);

  const handleTopLevelBack = () => setSelectedNavLinkItem(null);

  const handleCategoryClick = (data: string) => setSelectedCategory(data);

  const handleCategoryBack = () => setSelectedCategory('');

  const categories = (
    topMenu?.find(
      (menu, index) => menu?.component === 'ui.menu-section' && index === selectedNavLinkItem?.index
    ) as UIMenuSectionTypes
  )?.categories?.find((cat) => cat?.name === selectedCategory);

  useEffect(() => {
    Router.events.on('routeChangeStart', handleClose);

    return () => {
      Router.events.off('routeChangeStart', handleClose);
    };
  }, [handleClose]);

  return (
    <>
      <Portal container={closeIconRef.current}>
        <Fade in={showTopMenuDrawer}>
          <Box
            sx={{ position: 'fixed', left: '0', top: '0', zIndex: 1300, width: '15vw', mt: 0.375, cursor: 'pointer' }}
            onClick={handleClose}
          >
            <CloseIcon sx={{ fill: 'white', width: '100%', height: '100%' }} />
          </Box>
        </Fade>
      </Portal>
      <Drawer
        anchor="right"
        variant="temporary"
        onClose={handleClose}
        open={showTopMenuDrawer}
        data-testid="mobile-top-menu-drawer"
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            right: '0',
            left: 'auto',
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {/* Top Menu Home  */}
        <MobileTopMenuHome topMenu={topMenu} handleTopLevelClick={handleTopLevelClick} />

        <Drawer
          anchor="right"
          variant="temporary"
          onClose={handleClose}
          open={Boolean(selectedNavLinkItem?.component)}
          data-testid="mobile-top-menu-drawer-second-level"
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              left: 'auto',
              right: '0',
            },
          }}
        >
          {selectedNavLinkItem?.component === 'ui.sandbox-menu-section' && (
            <UIMenuSandboxSection handleBack={handleTopLevelBack} selectedNavLinkItem={selectedNavLinkItem} />
          )}
          {selectedNavLinkItem?.component === 'ui.menu-section' && (
            <UIMenuSection
              handleBack={handleTopLevelBack}
              handleCategoryClick={handleCategoryClick}
              selectedNavLinkItem={selectedNavLinkItem}
            />
          )}
        </Drawer>

        {/* Deepest Level for subcategory  */}
        <Drawer
          anchor="right"
          variant="temporary"
          onClose={handleClose}
          open={Boolean(selectedCategory?.length)}
          data-testid="mobile-top-menu-drawer-third-level"
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              left: 'auto',
              right: '0',
            },
          }}
        >
          <BackButton handleOnClick={handleCategoryBack} label={selectedCategory} />

          <Stack sx={{ padding: '0 21px' }}>
            <TopMenuMobileLink
              type="title"
              label={`Shop All ${categories?.name}`}
              href={`/product-category/${categories?.slug}`}
            />
            {categories?.subcategories?.map((subCat, index) => (
              <TopMenuMobileLink
                label={subCat?.name}
                href={`/product-category/${subCat?.slug}`}
                key={`ui.menu-section-${selectedNavLinkItem?.label}-subcategory-${index + 1}`}
              />
            ))}
          </Stack>
        </Drawer>
      </Drawer>
    </>
  );
};

export default DrawerAppBar;
